import { useState, useEffect, useRef } from "react";
import "../../styles/messages-embed.scss";
import MessageTabs from "../Messages/MessageTabs";
import SliderSearchBox from "../../components/slider-search-box";
import EmbedMessageDropdown from "../EmbedMessageDropdown";
import EmbedNotificationBell from "../../components/embed-notification-bell";
import { GuiButton } from "../../components/global-ui/gui-button";
import { GuiCheckbox } from "../../components/global-ui/gui-checkbox";
import Popover from "../../components/popover";
import { Modal } from "../../components/modal";
import AudioPlayer from "../../components/audio-player";
import { removeTags } from "../../utilities/constants";
import {
  TABS,
  tabToStatus,
  formattedDate,
  getInitials,
  alertTypeName,
  alertIcon,
  formatDescription,
} from "../../utilities/messages-helpers";
import actions from "../../actions/actions";
import { useUpdateState } from "../../utilities/utilities";
import { showToast } from "../../components/toast";
import { useSelector } from "react-redux";
import Card from "../../components/card";
import Grid from "../../components/grid";
import CircularProgress from "../../actions/CircularProgress";
import { formatDateISO } from "../../utilities/utilities";
import { GuiTooltip } from "./../../components/global-ui/gui-tooltip";

export default function MessagesEmbedPage() {
  const [audioUrls, setAudioUrls] = useState({});
  const [checkboxState, setCheckboxState] = useState({});
  const [selectedTab, setSelectedTab] = useState("all");
  const [isOpen, setIsOpen] = useState(false);
  const messageRef = useRef(null);
  // Tracks message ids already converted or in-flight, so a grid reload doesn't refire duplicate TTS requests
  const ttsRequestedRef = useRef(new Set());
  const [groupList, setGroupList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [alertTypes, setAlertTypes] = useState([]);
  // Prevents the Grid component from mounting (and firing its own API call)
  // before the ParishSoftToken is stored in localStorage.
  const [tokenReady, setTokenReady] = useState(false);

  const isBulkActionTab =
    selectedTab === "scheduled" || selectedTab === "drafts";
  const selectedCount = Object.values(checkboxState).filter(Boolean).length;
  const deleteEnabled = isBulkActionTab && selectedCount > 0;

  let isMobile = useSelector((store) => store.page.isMobile);

  let [state, updateState] = useUpdateState(() => {
    return {
      messageList: [],
      groupList: [],
      createdUserList: [],
      advanceFilter: {},
      filterPills: [],
      totalCount: 0,
      scheduledCount: 0,
      totalCredits: "",
      remainingCredits: "",
      billingCycle: "Monthly",
      gridReset: 0,
      isDeleteEnabled: false,
      showDeleteModal: false,
      isLoading: false,
      selectedMsg: 0,
      search: "",
      alertType: [
        { value: 0, label: "All" },
        { value: 1, label: "Voice" },
        { value: 2, label: "Email" },
        { value: 3, label: "SMS" },
        { value: 4, label: "VoiceIVR" },
        { value: 7, label: "RSS" },
      ],
    };
  });

  const tabs = TABS;

  function normalizeToken(rawToken) {
    if (!rawToken) return null;
    const trimmed = rawToken.trim();
    if (!trimmed) return null;
    return trimmed;
  }

  // Helper: extract everything after /messages-embed/ as one string
  function getTokenFromPath(pathname) {
    const parts = (pathname || "").split("/").filter(Boolean);
    const idx = parts.indexOf("messages-embed");
    if (idx >= 0 && parts.length > idx + 1) {
      const tail = parts.slice(idx + 1).join("/");
      try {
        return normalizeToken(decodeURIComponent(tail));
      } catch (_) {
        return normalizeToken(tail);
      }
    }
    return null;
  }

  // Helper: read token from query string (?jwt=... or ?token=...)
  function getTokenFromQuery(search) {
    const query = (search || "").replace(/^\?/, "");
    if (!query) return null;

    for (const segment of query.split("&")) {
      if (!segment) continue;

      const eqIndex = segment.indexOf("=");
      if (eqIndex === -1) continue;

      const key = segment.slice(0, eqIndex);
      const rawValue = segment.slice(eqIndex + 1);
      if (key !== "jwt" && key !== "token") continue;
      if (!rawValue) return null;

      try {
        const plusSafe = rawValue.replace(/\+/g, "%2B");
        return normalizeToken(decodeURIComponent(plusSafe));
      } catch (_) {
        return normalizeToken(rawValue);
      }
    }

    return null;
  }

  // Store token and check parishsoft access on component mount
  useEffect(() => {
    // Store token in localStorage as "ParishSoftToken" (query string first, then path)
    const tokenFromQuery = getTokenFromQuery(window.location.search);
    const tokenFromPath = getTokenFromPath(window.location.pathname);
    const token = tokenFromQuery || tokenFromPath;
    if (token) {
      localStorage.setItem("ParishSoftToken", token);
      // Token in the URL means this is a valid ParishSoft-initiated session
      // (covers direct URL access and incognito where isParishSoftUser flag isn't pre-set)
      localStorage.setItem("isParishSoftUser", "true");
    }

    // Check parishsoft access using stored flag (set by MessagingIframePage or URL token above)
    const isParishSoft = localStorage.getItem("isParishSoftUser");

    // If the flag exists and is explicitly "false", redirect to home
    if (isParishSoft === null || isParishSoft === "false") {
      window.location.href = "/";
      return;
    }

    // Mark token as ready — this gates the Grid from mounting until the
    // token is in localStorage, preventing the Grid's own useEffect from
    // firing an API call with an empty/stale token (child effects run before parent effects).
    setTokenReady(true);
    loadMessages();
    loadAlertCredit();
  }, []);

  function onPageChanged(list) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCheckboxState({});
    updateState({ currentPage: list });
  }

  function renderGroupLabel(groupName) {
    const names = (groupName || "")
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);

    if (names.length === 0) return groupName;
    if (names.length <= 3) return names.join(", ");

    const shownNames = names.slice(0, 3).join(", ");
    const remainingCount = names.length - 3;

    return (
      <span
        title={names.join(", ")}
        style={{ cursor: "pointer" }}
        onClick={(e) => {
          e.stopPropagation();
          alert("Groups:\n" + names.join(", "));
        }}
      >
        {shownNames}
        <span style={{ marginLeft: 4, color: "#888" }}>
          {`+${remainingCount} more`}
        </span>
      </span>
    );
  }

  async function convertTTS(description) {
    const result = await actions.parishSoftApis.textToSpeech(description);
    return result;
  }

  function applyFilter(advanceFilter) {
    updateState({
      advanceFilter: advanceFilter,
      gridReset: state.gridReset + 1,
      selectedFilter: advanceFilter,
    });

    const newArray = [];
    let selectedGroup = groupList?.filter(
      (items) => items?.groupId === advanceFilter?.groupId,
    );
    let selectedUser = userList?.find(
      (item) => item?.createdUserId === advanceFilter?.senderId,
    );
    let selectedMessageType = alertTypes?.find(
      (item) => item?.value === advanceFilter?.messageType,
    );
    const filterActions = {
      dateRange: () => {
        if (advanceFilter?.startDate && advanceFilter?.endDate) {
          newArray.push({
            Date: `${formatDateISO(advanceFilter?.startDate)}-${formatDateISO(
              advanceFilter?.endDate,
            )}`,
          });
        }
      },
      messageType: () => {
        if (advanceFilter?.messageType) {
          newArray.push({ "Message Type": selectedMessageType?.label });
        }
      },
      group: () => {
        if (advanceFilter?.groupId) {
          newArray.push({
            Group: selectedGroup[0]?.groupName,
          });
        }
      },
      sender: () => {
        if (advanceFilter?.senderId) {
          newArray.push({
            From: selectedUser?.createdUserName,
          });
        }
      },
    };
    Object.keys(filterActions).forEach((key) => {
      filterActions[key]();
    });
    updateState({ filterPills: newArray });
  }

  function onMsgClickEmbed(id) {
    const msg = state.messageList.find((m) => m.id === id);
    if (msg && msg.categoryName === "SMS Inbound") {
      localStorage.setItem("smsInboundDetails", JSON.stringify(msg));
    }
    actions.parishSoftApis.loadAlertDetails(id);
  }

  async function convertVoice(messageBody, messageId) {
    try {
      const response = await convertTTS(messageBody);
      if (response.success) {
        setAudioUrls((prev) => ({
          ...prev,
          [messageId]: response?.fileUrl,
        }));
      } else {
        console.error("TTS conversion failed:", response.error);
        ttsRequestedRef.current.delete(messageId);
      }
    } catch (error) {
      console.error("Error during TTS conversion:", error);
      ttsRequestedRef.current.delete(messageId);
    }
  }

  async function loadRows(
    page,
    pageSize,
    skipTotal,
    tabId = selectedTab,
    advanceFilter = state.advanceFilter,
  ) {
    updateState({ isLoading: true });
    let search = state.search ? state.search.trim() : "";

    let payload = {
      status: tabToStatus(tabId),
      pageNum: page,
      pageSize: pageSize,
      search: search,
      advanceFilters: advanceFilter,
      sort: [],
      skipTotal: skipTotal,
    };

    let result = await actions.parishSoftApis.loadMessages(payload);

    if (!result || !result.success) {
      updateState({ isLoading: false });
      return result || { success: false, list: [] };
    }

    result.list.forEach((message) => {
      if (message.alertType === 1) {
        if (message.voiceFileUrl) {
          setAudioUrls((prev) => ({
            ...prev,
            [message.id]: message.voiceFileUrl,
          }));
        } else if (!ttsRequestedRef.current.has(message.id)) {
          ttsRequestedRef.current.add(message.id);
          convertVoice(message?.messageBody, message.id);
        }
      }
    });
    updateState({
      totalCount: result.totalCount,
      scheduledCount: result?.scheduledCount,
      messageList: result?.list,
      latestPage: result?.list,
      createdUserList: result?.createdUserList,
      groupList: result?.groupList,
      isLoading: false,
    });

    return result;
  }

  function getMenuOptionsForStatus(status) {
    switch (status) {
      case 1:
        return ["Use as Draft"]; // Sent
      case 2:
        return ["Delete", "Send Now", "Edit"]; // Scheduled
      case 3:
        return ["Delete", "Edit"]; // Draft
      default:
        return [];
    }
  }

  function mapMessageToAlert(message) {
    const groups = Array.isArray(message.groups)
      ? message.groups.map((g) => {
          if (typeof g !== "object") return g;

          const pickerType = (() => {
            switch (g.recipient_type) {
              case 1:
                return "member";
              case 2:
                return "family";
              case 3:
                return "memberWorkgroup";
              case 4:
                return "familyWorkgroup";
              default:
                return undefined;
            }
          })();

          const workgroupSelectionId =
            (pickerType === "memberWorkgroup" ||
              pickerType === "familyWorkgroup") &&
            g.group_code
              ? Number(g.group_code)
              : null;
          const pickerId =
            pickerType === "family" && g.familyDUID
              ? g.familyDUID
              : pickerType === "member" && g.memberDUID
                ? g.memberDUID
                : workgroupSelectionId || g.hgsGroupId || g.id;

          return {
            id: pickerId,
            hgsGroupId: g.hgsGroupId || g.id,
            familyDUID: g.familyDUID || null,
            memberDUID: g.memberDUID || null,
            group_code: g.group_code || null,
            name: g.name,
            _pickerType: pickerType,
            recipient_type: g.recipient_type,
          };
        })
      : [];

    return {
      id: message.status !== 1 ? message.id : 0,
      name: message.messageSubject || message.groupName || "",
      scheduledDate: message.scheduled || null,
      groups,
      hgsGroups: Array.from(
        new Map(
          groups
            .filter((g) => typeof g === "object" && g.hgsGroupId > 0)
            .map((g) => [g.hgsGroupId, { id: g.hgsGroupId, name: g.name }]),
        ).values(),
      ),
      isEmergency: message.isEmergency || false,
      status: message.status,
      attachments: message.attachments || [],
      alertType: message.alertType,
      message: message.messageBody,
      voiceFileUrl: message.voiceFileUrl,
      voiceFileIdentifier: message.voiceFileIdentifier,
    };
  }

  function handleTridotAction(action, message) {
    switch (action) {
      case "Delete":
        updateState({
          showDeleteModal: true,
          singleEntryDelete: true,
          singleEntryId: [{ alertId: message.id, groupId: message.groupId }],
          selectedMsg: 1,
          isDeleteEnabled: true,
        });
        break;
      case "Edit":
        const mapped = mapMessageToAlert(message);
        actions.embedAlerts.showEditAlert(mapped, "/messages-embed");
        break;
      case "Send Now": {
        const mapped = mapMessageToAlert(message);
        actions.embedAlerts.showEditAlert(mapped, "/messages-embed");
        break;
      }
      case "Use as Draft": {
        const mapped = mapMessageToAlert({ ...message, status: 3 });
        actions.embedAlerts.showEditAlert(mapped, "/messages-embed");
        break;
      }
      default:
        break;
    }
  }

  async function handleDelete() {
    updateState({ isLoading: true });
    let payload = state.singleEntryDelete
      ? state.singleEntryId
      : state.filteredCheckboxes;

    let response = await actions.parishSoftApis.bulkDelete(payload);
    if (response?.success) {
      showToast("success", response?.message || "Deleted successfully");
    } else {
      showToast("error", response?.message || "Delete failed");
    }

    updateState({
      gridReset: state.gridReset + 1,
      showDeleteModal: false,
      isLoading: false,
      singleEntryDelete: false,
      singleEntryId: [],
      selectedMsg: 0,
      isDeleteEnabled: false,
    });
    setCheckboxState({});
  }

  function setSearch(search) {
    updateState({ search: search, gridReset: state.gridReset + 1 });
  }

  const handleOutsideClick = (event) => {
    if (messageRef.current && !messageRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const loadAlertCredit = async () => {
    const result = await actions.parishSoftApis.getAlertCredit();
    if (result?.success) {
      updateState({
        totalCredits: result.totalCredits,
        remainingCredits: result.remainingCredits,
        billingCycle: result.billingCycle,
      });
    }
  };

  const loadMessages = async () => {
    let payload = {
      status: tabToStatus(selectedTab),
      pageNum: 1,
      pageSize: 20,
      search: "",
      advanceFilters: state.advanceFilter,
      sort: [],
      skipTotal: false,
    };

    let result = await actions.parishSoftApis.loadMessages(payload);
    if (result && result?.success) {
      updateState({
        totalCount: result?.totalCount,
        scheduledCount: result?.scheduledCount,
        messageList: result?.list,
        latestPage: result?.list,
        createdUserList: result?.createdUserList,
        groupList: result?.groupList,
        isLoading: false,
      });
      setGroupList(result?.groupList);
      setUserList(result?.createdUserList);
      setAlertTypes(state?.alertType);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("click", handleOutsideClick);
    } else {
      document.removeEventListener("click", handleOutsideClick);
    }

    // Cleanup event listener when component unmounts
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [isOpen]);
  const getSelectedCheckboxes = () => {
    const selectedCheckboxes = Object.entries(checkboxState)
      .filter(([key, value]) => value)
      .map(([key]) => {
        let [groupId, alertId] = key.split("_");
        const message = state.messageList.find((msg) => {
          return (
            msg.id === parseInt(alertId) && msg.groupId === parseInt(groupId)
          );
        });

        return message;
      });
    const filteredCheckboxes = selectedCheckboxes
      .filter((item) => item.status === 3 || item.status === 2)
      .map((items) =>
        (({ id, groupId }) => ({
          alertId: id,
          groupId: groupId,
        }))(items),
      );
    const totalSelected = selectedCheckboxes.length;
    updateState({
      isDeleteEnabled: filteredCheckboxes.length > 0,
      filteredCheckboxes: filteredCheckboxes,
      selectedMsg: totalSelected,
    });
    return filteredCheckboxes;
  };

  const handleTabChange = (tabId) => {
    setSelectedTab(tabId);
    updateState({ gridReset: state.gridReset + 1 });
    setCheckboxState({});
    updateState({ selectedMsg: 0, isDeleteEnabled: false });
  };

  const handleIconClick = (key) => {
    const keyMapping = {
      "Message Type": ["messageType"],
      Date: ["startDate", "endDate"],
      Group: ["groupId"],
      From: ["senderId"],
    };

    // Function to update values based on provided key
    const updateObjectByKey = (displayKey, obj) => {
      const keysToNullify = keyMapping[displayKey]; // Get the actual object key(s)

      if (keysToNullify) {
        let updatedObj = { ...obj };
        keysToNullify.forEach((key) => {
          if (updatedObj.hasOwnProperty(key)) {
            updatedObj[key] = null; // Set each matching key to null
          }
        });
        return updatedObj;
      }

      return obj; // Return the object as is if the key doesn't exist
    };

    let filteredObject = updateObjectByKey(key, state.advanceFilter);
    applyFilter(filteredObject);
  };

  function renderStatusBadge(status, isMobileBadge = false) {
    if (status === 2) {
      return (
        <GuiButton
          className={
            isMobileBadge
              ? "mobile-scheduled-alert-type-btn"
              : "scheduled-alert-type-btn"
          }
        >
          <span>
            <i className="fa fa-clock"></i>
            {!isMobileBadge && "Scheduled"}
          </span>
        </GuiButton>
      );
    }

    if (status === 3) {
      return (
        <GuiButton
          className={
            isMobileBadge
              ? "mobile-draft-alert-type-btn"
              : "draft-alert-type-btn"
          }
        >
          <span>
            <i className="fa fa-pen"></i>
            {!isMobileBadge && "Draft"}
          </span>
        </GuiButton>
      );
    }

    if (status === 5) {
      return (
        <GuiButton
          className={
            isMobileBadge
              ? "mobile-preparing-alert-type-btn"
              : "preparing-alert-type-btn"
          }
        >
          <span>
            <i className="fa fa-spinner"></i>
            Preparing
          </span>
        </GuiButton>
      );
    }

    return null;
  }

  useEffect(() => {
    getSelectedCheckboxes();
  }, [checkboxState]);

  return (
    <>
      <div className="messages-embed-page">
        <div className="page-content">
          <div className="page-layout-header display-flex flex-row">
            <h3>Messages</h3>
            <div className="page-layout-top-buttons">
              {state.totalCredits > 0 && (
                <div className="progress-container">
                  <div
                    className={
                      state.remainingCredits > 0
                        ? "progress-container-text"
                        : "progress-container-text-negative-credit"
                    }
                  >
                    {state.remainingCredits <= 0 && (
                      <>
                        <GuiTooltip
                          tooltip="Overage charges will apply for any new messages."
                          placement="top"
                        >
                          <i className="fa fa-exclamation-circle"></i>
                        </GuiTooltip>{" "}
                      </>
                    )}
                    {state.remainingCredits} Texts/Voice Messages Left This{" "}
                    {state.billingCycle === "Monthly" ? "Month" : "Year"}
                  </div>
                  <progress
                    value={state.remainingCredits}
                    max={state.totalCredits}
                    className={
                      state.remainingCredits > 0
                        ? "styled-progress-bar"
                        : "styled-progress-bar negative-credits"
                    }
                  ></progress>
                </div>
              )}
              <div className="mobile-title">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <EmbedNotificationBell ready={tokenReady} />
                  <EmbedMessageDropdown from={"/messages-embed"} />
                </div>
              </div>
            </div>
          </div>

          {isBulkActionTab && selectedCount > 0 && (
            <div className="messages-bulk-actions-bar">
              <div className="messages-bulk-actions-bar-selected">
                {selectedCount} Selected
              </div>
              <GuiButton className="bulk-delete-btn" disabled={!deleteEnabled}>
                {" "}
                <i className="fa fa-trash"></i> Delete{" "}
              </GuiButton>
            </div>
          )}

          <div style={!isMobile ? { position: "relative" } : {}}>
            <Card className="messages-card">
              <>
                {isMobile ? (
                  <>
                    <div className="modal-footer-hr-divider"></div>
                    <div className="messages-search-header">
                      <div className="messages-search-header-tabs">
                        <MessageTabs
                          tabs={tabs}
                          selectedTab={selectedTab}
                          onTabChange={handleTabChange}
                        />
                      </div>

                      <SliderSearchBox
                        createdUserList={state.createdUserList}
                        groupList={state.groupList}
                        applyFilter={applyFilter}
                        selectedFilter={state.selectedFilter}
                        onSearchChange={setSearch}
                        searching={state.loading}
                      />
                    </div>
                    <div className="filter-pill-container">
                      {state?.filterPills?.map((item, index) => {
                        const key = Object.keys(item)[0]; // Get the key name
                        const value = item[key]; // Get the value
                        return (
                          <GuiButton
                            className="filter-pill-btn"
                            key={index}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="filter-pill-btn-content">
                              <div className="filter-pill-btn-content-key">
                                {key}:
                              </div>
                              <div className="filter-pill-btn-content-value">
                                {value}
                              </div>

                              <i
                                className="fa fa-close"
                                onClick={() => handleIconClick(key)}
                              ></i>
                            </div>
                          </GuiButton>
                        );
                      })}
                    </div>
                    {isBulkActionTab && state.selectedMsg > 0 && (
                      <>
                        <div className="modal-footer-hr-divider"></div>
                        <div className="actions-btn-mobile">
                          <div className="actions-btn-mobile-selected">
                            {state.selectedMsg} Selected
                          </div>
                          <GuiButton
                            className="bulk-delete-btn"
                            disabled={!state.isDeleteEnabled}
                            onClick={(e) =>
                              updateState({
                                showDeleteModal: true,
                              })
                            }
                          >
                            <i className="fa fa-trash"></i> Delete
                          </GuiButton>
                        </div>
                        <div className="modal-footer-hr-divider"></div>
                      </>
                    )}
                    <div
                      className={`messages-grid${
                        isBulkActionTab && state.selectedMsg > 0
                          ? " bulk-bar-visible"
                          : ""
                      }`}
                    >
                      {!state.isLoading && state.messageList.length === 0 && (
                        <div className="no-records-placeholder">
                          <i className="far fa-inbox"></i>
                          <span>No records found</span>
                        </div>
                      )}
                      <div
                        className={
                          !state.isLoading && state.messageList.length === 0
                            ? "grid-hidden"
                            : ""
                        }
                      >
                        {tokenReady && (
                          <Grid
                            itemTemplate={(g) => (
                              <div
                                className={`row${
                                  checkboxState[`${g.groupId}_${g.id}`]
                                    ? " selected-row"
                                    : ""
                                }`}
                                key={g.id}
                              >
                                <div className="check">
                                  <div className="check-row">
                                    <div className="check-title">
                                      {isBulkActionTab && (
                                        <GuiCheckbox
                                          name={`${g.groupId}_${g.id}`}
                                          value={
                                            checkboxState[
                                              `${g.groupId}_${g.id}`
                                            ] || false
                                          }
                                          //onChange={updateCheckboxState}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      )}
                                      <span className="initials">
                                        {(() => {
                                          const initials = getInitials(
                                            g.groupName,
                                          );
                                          return initials ? (
                                            initials
                                          ) : (
                                            <i
                                              className="icon fas fa-paper-plane"
                                              style={{
                                                color: "#ccc",
                                                fontSize: "1.2em",
                                              }}
                                            ></i>
                                          );
                                        })()}
                                      </span>
                                    </div>
                                    <div className="check-body">
                                      <div
                                        className="check-body-name body-hover"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onMsgClickEmbed(g.id);
                                        }}
                                      >
                                        <div className="check-body-name-header">
                                          {renderGroupLabel(g.groupName)}

                                          {renderStatusBadge(g.status, true)}

                                          <GuiButton className="alert-type-btn-mobile">
                                            <span>
                                              <i
                                                className={alertIcon(
                                                  g.alertType,
                                                )}
                                              ></i>
                                              {alertTypeName(g.alertType)}
                                            </span>
                                          </GuiButton>
                                        </div>
                                        <div
                                          className="check-body-actions"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                        >
                                          <span
                                            className={`check-body-date${
                                              g.status === 2
                                                ? "-scheduled"
                                                : g.status === 3
                                                  ? "-draft"
                                                  : g.status === 5
                                                    ? "-preparing"
                                                    : "-default"
                                            }`}
                                          >
                                            {formattedDate(g.scheduled)}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="check-body-details-mobile">
                                        <div
                                          className="check-body-details-mobile-header body-hover"
                                          // onClick={(e) => {
                                          //   e.stopPropagation();
                                          //   onMsgClick(g.id);
                                          // }}
                                        >
                                          <span className="msg-subject">
                                            {g.messageSubject}
                                          </span>
                                          {g.alertType === 1 ? (
                                            audioUrls[g.id] ? (
                                              <AudioPlayer
                                                src={audioUrls[g.id]}
                                                autoPlay={false}
                                              />
                                            ) : (
                                              <CircularProgress />
                                            )
                                          ) : (
                                            <span>
                                              {removeTags(
                                                formatDescription(
                                                  g.messageBody,
                                                ),
                                              )}
                                              {" ..."}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    &nbsp;
                                    <div className="tridot">
                                      <i
                                        style={{
                                          cursor: "pointer",
                                          width: "10px",
                                        }}
                                        className="fa fa-ellipsis-vertical mobile-action-icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateState({
                                            showActionsMenu: g.id,
                                          });
                                        }}
                                      ></i>
                                      <div className="actions">
                                        <Popover
                                          show={state.showActionsMenu === g.id}
                                          className="no-arrow anchor-right popover-menu actions-menu"
                                          onHide={() =>
                                            updateState("showActionsMenu", null)
                                          }
                                        >
                                          {getMenuOptionsForStatus(
                                            g.status,
                                          ).map((option) => (
                                            <div
                                              key={option}
                                              className="menu-item"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                updateState({
                                                  showActionsMenu: null,
                                                });
                                                handleTridotAction(option, g);
                                              }}
                                            >
                                              {option}
                                            </div>
                                          ))}
                                        </Popover>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            mobileItemHeight={65}
                            reset={state.gridReset}
                            pageSize={20}
                            onItemsNeeded={loadRows}
                            onPageChanged={onPageChanged}
                          ></Grid>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="messages-search-header">
                      <SliderSearchBox
                        createdUserList={state.createdUserList}
                        groupList={state.groupList}
                        applyFilter={applyFilter}
                        selectedFilter={state.selectedFilter}
                        onSearchChange={setSearch}
                        searching={state.isLoading}
                      />

                      <div className="messages-search-header-tabs">
                        <MessageTabs
                          tabs={tabs}
                          selectedTab={selectedTab}
                          onTabChange={handleTabChange}
                        />
                      </div>
                    </div>
                    {/* <div> */}
                    <div className="filter-pill-container">
                      {state.filterPills.map((item, index) => {
                        const key = Object.keys(item)[0]; // Get the key name
                        const value = item[key]; // Get the value
                        return (
                          <GuiButton
                            className="filter-pill-btn"
                            key={index}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="filter-pill-btn-content">
                              <div className="filter-pill-btn-content-key">
                                {key}:
                              </div>
                              <div className="filter-pill-btn-content-value">
                                {value}
                              </div>

                              <i
                                className="fa fa-close"
                                onClick={() => handleIconClick(key)}
                              ></i>
                            </div>
                          </GuiButton>
                        );
                      })}
                    </div>
                    <div className="modal-footer-hr-divider"></div>
                    {isBulkActionTab && state.selectedMsg > 0 && (
                      <div className="messages-bulk-actions-bar">
                        <div className="messages-bulk-actions-bar-selected">
                          {state.selectedMsg} Selected
                        </div>
                        <div className="vr-divider">
                          <GuiButton
                            className="bulk-delete-btn"
                            disabled={!state.isDeleteEnabled}
                            onClick={(e) => {
                              e.stopPropagation();
                              updateState({
                                showDeleteModal: true,
                              });
                            }}
                          >
                            <i className="fa fa-trash"></i> Delete
                          </GuiButton>
                        </div>
                      </div>
                    )}
                    <div className="modal-footer-hr-divider"></div>
                    <div className="messages-grid">
                      {!state.isLoading && state.messageList.length === 0 && (
                        <div className="no-records-placeholder">
                          <i className="far fa-inbox"></i>
                          <span>No records found</span>
                        </div>
                      )}
                      <div
                        className={
                          !state.isLoading && state.messageList.length === 0
                            ? "grid-hidden"
                            : ""
                        }
                      >
                        {tokenReady && (
                          <Grid
                            itemTemplate={(g) => (
                              <div className="row" key={g.id}>
                                <div className="check">
                                  <div className="check-row">
                                    <div className="check-title">
                                      {isBulkActionTab && (
                                        <GuiCheckbox
                                          name={`${g.groupId}_${g.id}`}
                                          key={g.id}
                                          value={
                                            checkboxState[
                                              `${g.groupId}_${g.id}`
                                            ] || false
                                          }
                                          //onChange={updateCheckboxState}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      )}
                                      <span className="initials">
                                        {(() => {
                                          const initials = getInitials(
                                            g.groupName,
                                          );
                                          return initials ? (
                                            initials
                                          ) : (
                                            <i
                                              className="icon fas fa-paper-plane"
                                              style={{
                                                color: "#ccc",
                                                fontSize: "1.2em",
                                              }}
                                            ></i>
                                          );
                                        })()}
                                      </span>
                                    </div>

                                    <div className="check-body">
                                      <div
                                        className="body-hover"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onMsgClickEmbed(g.id);
                                        }}
                                      >
                                        <div className="check-body-name">
                                          {renderGroupLabel(g.groupName)}
                                        </div>
                                        <div
                                          className="check-body-details"
                                          style={{
                                            cursor: "pointer",
                                          }}
                                        >
                                          <div className="check-body-details-content">
                                            {renderStatusBadge(g.status)}

                                            <GuiButton className="alert-type-btn">
                                              <span>
                                                <i
                                                  className={alertIcon(
                                                    g.alertType,
                                                  )}
                                                ></i>
                                                {alertTypeName(g.alertType)}
                                              </span>
                                            </GuiButton>
                                            <span className="msg-subject">
                                              {g.messageSubject}
                                            </span>
                                            {g.alertType === 1 ? (
                                              audioUrls[g.id] ? (
                                                <AudioPlayer
                                                  src={audioUrls[g.id]}
                                                  autoPlay={false}
                                                />
                                              ) : (
                                                <CircularProgress />
                                              )
                                            ) : (
                                              <span>
                                                {removeTags(
                                                  formatDescription(
                                                    g.messageBody,
                                                  ),
                                                )}
                                                {" ..."}
                                              </span>
                                            )}
                                          </div>
                                          <div
                                            className="check-body-actions"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <span
                                              className={`check-body-date${
                                                g.status === 2
                                                  ? "-scheduled"
                                                  : g.status === 3
                                                    ? "-draft"
                                                    : g.status === 5
                                                      ? "-preparing"
                                                      : "-default"
                                              }`}
                                            >
                                              {formattedDate(g.scheduled)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="tridot">
                                      <div>
                                        <i
                                          style={{
                                            cursor: "pointer",
                                            width: "10px",
                                          }}
                                          className="fa fa-ellipsis-vertical mobile-action-icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateState({
                                              showActionsMenu: g.id,
                                            });
                                          }}
                                        ></i>
                                      </div>
                                      <div className="actions">
                                        <Popover
                                          show={state.showActionsMenu === g.id}
                                          className="no-arrow anchor-right popover-menu actions-menu"
                                          onHide={() =>
                                            updateState("showActionsMenu", null)
                                          }
                                        >
                                          {getMenuOptionsForStatus(
                                            g.status,
                                          ).map((option) => (
                                            <div
                                              key={option}
                                              className="menu-item"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                updateState({
                                                  showActionsMenu: null,
                                                });
                                                handleTridotAction(option, g);
                                              }}
                                            >
                                              {option}
                                            </div>
                                          ))}
                                        </Popover>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            mobileItemHeight={65}
                            reset={state.gridReset}
                            pageSize={20}
                            onItemsNeeded={loadRows}
                            onPageChanged={onPageChanged}
                            pageName="messages"
                          ></Grid>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            </Card>
            {state?.isLoading && (
              <div className="messages-loader">
                <CircularProgress />
              </div>
            )}
          </div>
        </div>
      </div>
      {state.showDeleteModal && (
        <Modal className="modal bulk-delete-modal">
          <div className="bulk-delete-modal-header">
            <span>Delete Message(s)?</span>
            <div
              onClick={() => {
                updateState({ showDeleteModal: false });
              }}
            >
              <i className="fa fa-close close-primary-icon cursor-pointer"></i>
            </div>
          </div>
          <div className="modal-footer-hr-divider"></div>
          <div className="bulk-delete-modal-content">
            <span>
              This action cannot be undone. Please note only Scheduled and Draft
              messages can be deleted. Are you sure you want to delete these{" "}
              {state?.selectedMsg} message(s)?
            </span>
          </div>
          <div className="bulk-delete-modal-footer">
            <GuiButton
              className="btn-outline"
              disabled={state?.isLoading}
              onClick={() =>
                updateState({
                  showDeleteModal: false,
                  singleEntryDelete: false,
                  singleEntryId: [],
                })
              }
            >
              Cancel
            </GuiButton>
            <GuiButton
              className="warning"
              disabled={state?.isLoading}
              onClick={handleDelete}
            >
              {`Delete ${state.selectedMsg} Message(s)`}
            </GuiButton>
          </div>
        </Modal>
      )}
    </>
  );
}
